import {
  ReactNode,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Download, Eye, Upload } from "lucide-react";
import { createPortal } from "react-dom";
import {
  AgencyDocument,
  AgencyDocumentsApi,
  AgencyFile,
  AgencyRegister,
  AgencyVersion,
} from "./agencyDocumentsApi";
import "./agencyDocuments.css";

export const documentStateLabel = (value: string) =>
  ({
    AWAITING_SIGNATURE: "Awaiting signature",
    PENDING_CONFIRMATION: "Pending confirmation",
    FUTURE_EFFECTIVE: "Future effective",
    ACTIVE: "Active",
    EXPIRING: "Expiring",
    EXPIRED: "Expired",
    RENEWED: "Renewed",
    SUPERSEDED: "Superseded",
    ARCHIVED: "Archived",
  })[value] || value;
const needsSignature = (d: AgencyDocument) =>
  d.requiresSignature ?? d.category === "CONTRACT";
const categoryLabel = (value: string) =>
  ({
    CONTRACT: "Contract",
    AGREEMENT: "Agreement",
    REGISTRATION: "Company registration",
    CERTIFICATE: "Certificate",
    IDENTIFICATION: "Identification",
    OTHER: "Other",
  })[value] || value;
const date = (value?: string | null) =>
  value
    ? new Date(`${value.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Africa/Lagos",
      })
    : "—";
const timestamp = (value: string) =>
  new Date(value).toLocaleString("en-GB", { timeZone: "Africa/Lagos" });
export const documentError = (e: any) =>
  e?.response?.data?.error ||
  e?.message ||
  "Unable to complete the request. Please retry.";
export function DocumentStatus({ status }: { status: string }) {
  return (
    <span
      className={`agency-documents__status ${["EXPIRING", "PENDING_CONFIRMATION"].includes(status) ? "agency-documents__status--warning" : ["ACTIVE", "FUTURE_EFFECTIVE"].includes(status) ? "agency-documents__status--success" : status === "EXPIRED" ? "agency-documents__status--danger" : ""}`}
    >
      {documentStateLabel(status)}
    </span>
  );
}
export function DocumentPagination({
  page,
  pages,
  label,
  change,
  disabled,
  alwaysShow,
}: {
  page: number;
  pages: number;
  label: string;
  change: (n: number) => void;
  disabled?: boolean;
  alwaysShow?: boolean;
}) {
  if (pages <= 1 && !alwaysShow) return null;
  return (
    <nav
      className="d-flex gap-3 align-items-center justify-content-end p-3"
      aria-label={label}
    >
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        disabled={disabled || page <= 1}
        onClick={() => change(page - 1)}
      >
        Previous
      </button>
      <small>
        Page {page} of {Math.max(1, pages)}
      </small>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        disabled={disabled || page >= Math.max(1, pages)}
        onClick={() => change(page + 1)}
      >
        Next
      </button>
    </nav>
  );
}
export function AgencyDocumentsWorkspace(props: {
  api: AgencyDocumentsApi;
  accountKey: string;
  agencyId?: number;
  partnershipId?: number;
  onChanged?: () => void;
}) {
  return (
    <AgencyDocumentsSession
      key={`${props.accountKey}:${props.agencyId || "mine"}:${props.partnershipId || "all"}`}
      {...props}
    />
  );
}
function AgencyDocumentsSession({
  api,
  agencyId,
  partnershipId,
  onChanged,
}: {
  api: AgencyDocumentsApi;
  accountKey: string;
  agencyId?: number;
  partnershipId?: number;
  onChanged?: () => void;
}) {
  const [data, setData] = useState<AgencyRegister | null>(null),
    [error, setError] = useState(""),
    [denied, setDenied] = useState(false),
    [busy, setBusy] = useState(false),
    [page, setPage] = useState(1),
    [includeArchived, setIncludeArchived] = useState(false),
    [loading, setLoading] = useState(true);
  const [settingsDocument, setSettingsDocument] =
    useState<AgencyDocument | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState<AgencyDocument | null>(null),
    [versionPage, setVersionPage] = useState(1),
    [eventPage, setEventPage] = useState(1),
    [historyBusy, setHistoryBusy] = useState(false);
  const [form, setForm] = useState<{
    mode: "upload" | "replace" | "signed" | "confirm" | "archive";
    doc?: AgencyDocument;
    version?: AgencyVersion;
  } | null>(null);
  const [preview, setPreview] = useState<{
      url: string;
      file: AgencyFile;
    } | null>(null),
    [fileBusy, setFileBusy] = useState(false),
    [notice, setNotice] = useState("");
  const alive = useRef(true),
    controller = useRef<AbortController>(),
    detailController = useRef<AbortController>(),
    fileSequence = useRef(0),
    fileLock = useRef(false),
    mutationLock = useRef(false),
    dialog = useRef<HTMLDivElement>(null),
    previewUrl = useRef("");
  const selection = useRef<{
    id: number;
    versionPage: number;
    eventPage: number;
  } | null>(null);
  const revokeAccess = useCallback((e: unknown) => {
    controller.current?.abort();
    detailController.current?.abort();
    fileSequence.current++;
    setDenied(true);
    setData(null);
    setSelected(null);
    setSettingsDocument(null);
    selection.current = null;
    setForm(null);
    setPreview(null);
    setLoading(false);
    setHistoryBusy(false);
    setError(documentError(e));
    if (previewUrl.current) {
      URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = "";
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
      detailController.current?.abort();
      fileSequence.current++;
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    };
  }, []);
  const clearHistory = () => {
    detailController.current?.abort();
    selection.current = null;
    setSelected(null);
    setHistoryBusy(false);
  };
  const readHistory = useCallback(
    async (active: { id: number; versionPage: number; eventPage: number }) => {
      if (!alive.current) return;
      detailController.current?.abort();
      const control = new AbortController();
      detailController.current = control;
      setHistoryBusy(true);
      try {
        const result = await api.detail(
          active.id,
          active.versionPage,
          active.eventPage,
          control.signal,
        );
        if (
          !control.signal.aborted &&
          alive.current &&
          selection.current === active
        ) {
          setSelected(result);
          setVersionPage(active.versionPage);
          setEventPage(active.eventPage);
        }
      } catch (e: any) {
        if (
          !control.signal.aborted &&
          alive.current &&
          selection.current === active
        ) {
          setError(documentError(e));
          if ([401, 403, 404].includes(e?.response?.status)) revokeAccess(e);
        }
      } finally {
        if (
          !control.signal.aborted &&
          alive.current &&
          detailController.current === control
        )
          setHistoryBusy(false);
      }
    },
    [api, revokeAccess],
  );
  const load = useCallback(async () => {
    if (!alive.current) return;
    controller.current?.abort();
    const control = new AbortController();
    controller.current = control;
    setLoading(true);
    try {
      const result = await api.register(
        agencyId,
        page,
        control.signal,
        includeArchived,
        { search, category, partnershipId },
      );
      if (control.signal.aborted || !alive.current) return;
      if (page > 1 && page > Math.max(1, result.pagination.pages)) {
        setPage(Math.max(1, result.pagination.pages));
        return;
      }
      setData(result);
      setDenied(false);
      setError("");
      if (selection.current) await readHistory(selection.current);
    } catch (e: any) {
      if (control.signal.aborted || !alive.current) return;
      setError(documentError(e));
      if ([401, 403, 404].includes(e?.response?.status)) revokeAccess(e);
    } finally {
      if (!control.signal.aborted && alive.current) setLoading(false);
    }
  }, [
    api,
    agencyId,
    partnershipId,
    page,
    includeArchived,
    search,
    category,
    revokeAccess,
    readHistory,
  ]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") void load();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);
  useEffect(() => {
    if (!data || !form || busy) return;
    const c = data.capabilities;
    const current =
      form.doc && data.documents.find((d) => d.id === form.doc!.id);
    const changed =
      form.doc &&
      (!current ||
        current.archivedAt ||
        current.latestVersion?.id !== form.doc.latestVersion?.id ||
        current.latestVersion?.signedEvidence?.id !==
          form.doc.latestVersion?.signedEvidence?.id ||
        current.latestVersion?.confirmedAt !==
          form.doc.latestVersion?.confirmedAt);
    if (
      !c.canManage ||
      changed ||
      (form.mode === "confirm" && !c.canConfirmSigned) ||
      (form.doc &&
        needsSignature(form.doc) &&
        ["replace", "archive"].includes(form.mode) &&
        !c.canIssueContracts)
    )
      setForm(null);
  }, [data, form, busy]);
  const select = async (id: number, vp = 1, ep = 1) => {
    const active = { id, versionPage: vp, eventPage: ep };
    selection.current = active;
    await readHistory(active);
  };
  const mutate = async (action: () => Promise<unknown>) => {
    if (mutationLock.current) return;
    mutationLock.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      if (!alive.current) return;
      setForm(null);
      setNotice("Changes saved.");
      if (form?.mode === "archive" && !includeArchived) clearHistory();
      else if (selected) await select(selected.id);
      await load();
      if (alive.current) onChanged?.();
    } catch (e: any) {
      if (alive.current) {
        setError(documentError(e));
        if ([401, 403, 404].includes(e?.response?.status)) revokeAccess(e);
      }
      throw e;
    } finally {
      mutationLock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const openFile = async (file: AgencyFile, download = false) => {
    if (fileLock.current) return;
    fileLock.current = true;
    setFileBusy(true);
    const sequence = ++fileSequence.current;
    try {
      const blob = await api.file(file, download);
      if (!alive.current || sequence !== fileSequence.current) return;
      const url = URL.createObjectURL(blob);
      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download = file.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
        previewUrl.current = url;
        setPreview({ url, file });
      }
    } catch (e: any) {
      if (alive.current && sequence === fileSequence.current) {
        setError(documentError(e));
        if ([401, 403, 404].includes(e?.response?.status)) revokeAccess(e);
      }
    } finally {
      fileLock.current = false;
      if (alive.current) setFileBusy(false);
    }
  };
  const closePreview = () => {
    setPreview(null);
    if (previewUrl.current) {
      URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = "";
    }
  };
  useEffect(() => {
    if (!preview) return;
    const previous = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePreview();
      }
      if (event.key === "Tab") {
        const nodes = dialog.current?.querySelectorAll<HTMLElement>(
          'button, a, iframe, [tabindex="0"]',
        );
        if (!nodes?.length) return;
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      document.body.style.overflow = previousOverflow;
      previous?.isConnected && previous.focus();
    };
  }, [preview]);
  const fileActions = (file: AgencyFile) => (
    <div className="agency-documents__actions">
      <button
        type="button"
        className="btn btn-sm btn-outline-primary p-1"
        title={`Preview ${file.fileName}`}
        aria-label={`Preview ${file.fileName}`}
        disabled={fileBusy}
        onClick={() => void openFile(file)}
      >
        <Eye size={16} />
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary p-1"
        title={`Download ${file.fileName}`}
        aria-label={`Download ${file.fileName}`}
        disabled={fileBusy}
        onClick={() => void openFile(file, true)}
      >
        <Download size={16} />
      </button>
    </div>
  );
  if (!data)
    return (
      <section className="agency-documents">
        <h1>Agency Documents</h1>
        {loading ? (
          <p role="status">Loading documents…</p>
        ) : (
          <div className="card p-4">
            <h2>
              {denied ? "Access unavailable" : "Unable to load documents"}
            </h2>
            <p role="alert" className="mt-2">
              {error}
            </p>
            {denied && (
              <p className="text-muted small">
                Sub-accounts require explicit access from the main agency
                account.
              </p>
            )}
            <button
              className="btn btn-outline-primary align-self-start"
              onClick={() => void load()}
            >
              Retry
            </button>
          </div>
        )}
      </section>
    );
  const caps = data.capabilities;
  const canReplace = (d: AgencyDocument) =>
    caps.canManage &&
    !d.archivedAt &&
    (!needsSignature(d) || caps.canIssueContracts) &&
    (!d.partnershipId || data.isAdmin);
  const versionSummary = (v: AgencyVersion) => (
    <>
      <span className="text-muted small">
        v{v.number} ·{" "}
        {v.doesNotExpire ? "Does not expire" : `Expires ${date(v.expiresOn)}`}
      </span>
      {v.effectiveDate && (
        <div className="text-muted small">
          Effective {date(v.effectiveDate)}
        </div>
      )}
    </>
  );
  return (
    <section
      className={`agency-documents${partnershipId ? " agency-documents--embedded" : ""}`}
      aria-busy={busy}
    >
      <header className="d-flex justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h1>Agency Documents</h1>
          <p className="text-muted small mb-0">
            {data.agency.name}
            {data.agency.country ? `, ${data.agency.country}` : ""}
          </p>
        </div>
        {caps.canManageAccess && data.grants.length > 0 && (
          <a
            href="#agency-document-access"
            className="btn btn-sm btn-outline-secondary align-self-start"
          >
            Subaccount access
          </a>
        )}
      </header>
      <form
        className="d-flex flex-wrap gap-2 align-items-end mb-3"
        onSubmit={(event) => {
          event.preventDefault();
          clearHistory();
          setPage(1);
          setSearch(searchInput.trim());
        }}
      >
        <label className="flex-grow-1 small">
          Search documents
          <input
            type="search"
            className="form-control form-control-sm mt-1"
            placeholder="Search by document title"
            maxLength={200}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            disabled={busy}
          />
        </label>
        <label className="small">
          Category
          <select
            className="form-select form-select-sm mt-1"
            value={category}
            disabled={busy}
            onChange={(event) => {
              clearHistory();
              setPage(1);
              setCategory(event.target.value);
            }}
          >
            <option value="">All categories</option>
            {[
              "CONTRACT",
              "AGREEMENT",
              "REGISTRATION",
              "CERTIFICATE",
              "IDENTIFICATION",
              "OTHER",
            ].map((value) => (
              <option key={value} value={value}>
                {categoryLabel(value)}
              </option>
            ))}
          </select>
        </label>
        <button
          className="btn btn-sm btn-outline-primary"
          disabled={busy || loading}
        >
          Search
        </button>
        {(search || category || searchInput) && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={busy}
            onClick={() => {
              clearHistory();
              setPage(1);
              setSearchInput("");
              setSearch("");
              setCategory("");
            }}
          >
            Clear
          </button>
        )}
      </form>
      <p className="small text-muted" role="status">
        {data.pagination.total}{" "}
        {data.pagination.total === 1 ? "document" : "documents"}
        {search || category ? " matching filters" : ""}
      </p>
      <label className="form-check form-switch mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          checked={includeArchived}
          disabled={busy}
          onChange={(e) => {
            clearHistory();
            setForm(null);
            setPage(1);
            setIncludeArchived(e.target.checked);
          }}
        />
        <span className="form-check-label">Include archived</span>
      </label>
      {data.agency.countryIssue && (
        <p className="alert alert-warning small">{data.agency.countryIssue}</p>
      )}
      {error && (
        <div
          role="alert"
          className="alert alert-danger d-flex align-items-start gap-3"
        >
          <span className="agency-documents__text flex-grow-1">{error}</span>
          <button
            className="btn btn-sm btn-outline-danger"
            disabled={busy}
            onClick={() => void load()}
          >
            Refresh
          </button>
        </div>
      )}
      {notice && (
        <p role="status" className="text-success small">
          {notice}
        </p>
      )}
      {!caps.canManage && (
        <p className="alert alert-light border small">
          Read-only access. Preview, download and history are available. Changes
          require management permission.
        </p>
      )}
      <div className="row g-4">
        <div className="col-lg-8">
          <div className="card mb-4">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center gap-2">
              <h2>Current Documents</h2>
              {caps.canManage && (
                <button
                  className="btn btn-sm btn-primary"
                  disabled={busy || loading}
                  onClick={() => {
                    setForm({ mode: "upload" });
                    setError("");
                  }}
                >
                  <Upload size={15} className="me-1" />
                  Upload new
                </button>
              )}
            </div>
            {!data.documents.length && (
              <div className="p-4">
                <h3>
                  {search || category
                    ? "No matching documents"
                    : "No documents yet"}
                </h3>
                <p className="text-muted small mb-0">
                  {search || category
                    ? "Try a different search or clear the filters."
                    : caps.canManage
                      ? "Upload your first organisational document."
                      : "No organisational documents have been added."}
                </p>
              </div>
            )}
            {data.documents.map((d) => {
              const pending =
                !d.archivedAt &&
                needsSignature(d) &&
                !["SUPERSEDED", "RENEWED", "ARCHIVED"].includes(
                  d.latestVersion?.status || "",
                ) &&
                d.latestVersion?.id !== d.currentVersion?.id
                  ? d.latestVersion
                  : null;
              const v = d.currentVersion || d.latestVersion;
              const primaryFile =
                v?.confirmedAt && v.signedEvidence ? v.signedEvidence : v;
              return (
                <article
                  key={d.id}
                  className={`agency-documents__row agency-documents__entry ${d.status === "EXPIRING" ? "agency-documents__expiring" : ""}`}
                >
                  <div className="agency-documents__text">
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <span className="agency-documents__row-title">
                        {d.title}
                      </span>
                      <DocumentStatus status={pending?.status || d.status} />
                    </div>
                    <div className="text-muted small">
                      {categoryLabel(d.category)}
                      {d.partnershipId
                        ? ` · ${d.partnership?.institutionName || "Partnership-linked"}`
                        : " · Agency-wide"}
                      {needsSignature(d) ? " · Signature required" : ""}
                    </div>
                    {pending && d.currentVersion && (
                      <p className="small text-muted mb-1">
                        Previously accepted version{" "}
                        <DocumentStatus status={d.currentVersion.status} />
                      </p>
                    )}
                    {v && versionSummary(v)}
                    {v && (
                      <div className="text-muted small agency-documents__text">
                        {primaryFile?.fileName}
                      </div>
                    )}
                  </div>
                  <div className="agency-documents__actions">
                    {primaryFile && fileActions(primaryFile)}
                    <button
                      className="btn btn-sm btn-link px-1"
                      disabled={historyBusy || busy}
                      onClick={() => void select(d.id)}
                    >
                      History
                    </button>
                    {caps.canManageScope && api.settings && (
                      <button
                        className="btn btn-sm btn-link px-1"
                        disabled={busy || loading}
                        onClick={() => setSettingsDocument(d)}
                      >
                        Scope & signature
                      </button>
                    )}
                    {canReplace(d) && (
                      <button
                        className="btn btn-sm btn-link px-1"
                        disabled={busy || loading}
                        onClick={() => {
                          setForm({ mode: "replace", doc: d });
                          setError("");
                        }}
                      >
                        {needsSignature(d) ? "Prepare renewal" : "Replace"}
                      </button>
                    )}
                  </div>
                  {pending && (
                    <div className="agency-documents__pending">
                      {d.currentVersion && (
                        <div className="agency-documents__pending-summary">
                          <h3>Signing and renewals</h3>
                          <DocumentStatus status={pending.status} />
                          {versionSummary(pending)}
                          <div className="text-muted small">
                            {pending.fileName}
                          </div>
                        </div>
                      )}
                      {pending.confirmedAt && (
                        <p className="small text-muted mb-2">
                          Signed evidence confirmed. Activates on the effective
                          date.
                        </p>
                      )}
                      <div className="agency-documents__actions">
                        {d.currentVersion && fileActions(pending)}
                        {!pending.confirmedAt &&
                          (caps.canReturnSigned ?? caps.canManage) && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              disabled={busy || loading}
                              onClick={() => {
                                setForm({
                                  mode: "signed",
                                  doc: d,
                                  version: pending,
                                });
                                setError("");
                              }}
                            >
                              Return signed copy
                            </button>
                          )}
                        {caps.canConfirmSigned &&
                          pending.signedEvidence &&
                          !pending.confirmedAt && (
                            <button
                              className="btn btn-sm btn-primary"
                              disabled={busy || loading}
                              onClick={() => {
                                setForm({
                                  mode: "confirm",
                                  doc: d,
                                  version: pending,
                                });
                                setError("");
                              }}
                            >
                              Review signed return
                            </button>
                          )}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
            <DocumentPagination
              page={page}
              pages={data.pagination.pages}
              label="Document register pages"
              alwaysShow
              disabled={busy || loading}
              change={(n) => {
                clearHistory();
                setForm(null);
                setPage(n);
              }}
            />
          </div>
        </div>
        <aside className="col-lg-4">
          {!form && (
            <div className="card p-3 mb-4">
              <p className="small text-muted mb-0">
                A private space for your agency’s documents, agreements and
                certificates.
              </p>
            </div>
          )}
          {caps.canManageAccess && (
            <div className="card mb-4" id="agency-document-access">
              <div className="p-3 border-bottom">
                <h2>Subaccount Permissions</h2>
              </div>
              <div className="p-3">
                <p className="small text-muted">
                  Grant explicit access to organisational documents, including
                  identification records. None revokes access.
                </p>
                {!data.grants.length && (
                  <p className="small text-muted mb-0">
                    No sub-accounts in this agency.
                  </p>
                )}
                {data.grants.map((g) => (
                  <GrantControl
                    key={`${g.userId}:${Number(g.canView)}:${Number(g.canManage)}`}
                    grant={g}
                    disabled={busy}
                    save={(access, operationId) =>
                      mutate(() =>
                        api.grant(
                          data.agency.id,
                          g.userId,
                          operationId,
                          access,
                        ),
                      )
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
      {(historyBusy || selected) && (
        <DocumentHistoryDrawer
          busy={busy}
          suspended={!!preview || !!form || !!settingsDocument}
          close={clearHistory}
          footer={
            selected && canReplace(selected) ? (
              <button
                className="btn btn-sm btn-outline-secondary w-100 py-2"
                disabled={busy || loading}
                onClick={() => setForm({ mode: "archive", doc: selected })}
              >
                Archive document
              </button>
            ) : undefined
          }
        >
          {!selected && (
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
              <h2>Document history</h2>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={busy}
                onClick={clearHistory}
              >
                Close
              </button>
            </div>
          )}
          {historyBusy && <p role="status">Loading history…</p>}
          {error && (
            <div className="alert alert-danger m-3" role="alert">
              {error}
            </div>
          )}
          {selected && (
            <div>
              <div className="p-3 border-bottom d-flex gap-2 justify-content-between align-items-center">
                <h2
                  id="agency-document-history-title"
                  className="agency-documents__text"
                >
                  {selected.title} — history
                </h2>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={busy}
                  onClick={() => {
                    clearHistory();
                  }}
                >
                  Close
                </button>
              </div>
              <div className="agency-documents__history-body">
                <p className="small text-muted mb-4">
                  Previous versions and audit evidence are retained. All times
                  shown in Africa/Lagos.
                </p>
                <h3 className="agency-documents__history-section">
                  Version history
                </h3>
                {selected.versions.map((v) => (
                  <article
                    key={v.id}
                    className="agency-documents__history-item agency-documents__version"
                  >
                    <div className="agency-documents__history-heading">
                      <div className="agency-documents__history-content">
                        <div className="agency-documents__version-label">
                          <strong>
                            v{v.number}
                            {v.isCurrent ? " · Current" : ""}
                          </strong>
                          <DocumentStatus status={v.status} />
                        </div>
                        <h4 className="agency-documents__history-filename">
                          {v.fileName}
                        </h4>
                      </div>
                      <div className="agency-documents__history-actions">
                        {fileActions(v)}
                      </div>
                    </div>
                    <dl className="agency-documents__history-metadata">
                      <div>
                        <dt>Issued</dt>
                        <dd>{date(v.issueDate)}</dd>
                      </div>
                      {v.effectiveDate && (
                        <div>
                          <dt>Effective</dt>
                          <dd>{date(v.effectiveDate)}</dd>
                        </div>
                      )}
                      <div>
                        <dt>Expires</dt>
                        <dd>
                          {v.doesNotExpire
                            ? "Does not expire"
                            : date(v.expiresOn)}
                        </dd>
                      </div>
                    </dl>
                    <p className="agency-documents__history-byline">
                      Uploaded by {v.uploadedByName}
                      {v.createdAt ? ` · ${timestamp(v.createdAt)}` : ""}
                    </p>
                    {v.signedEvidence && (
                      <div className="agency-documents__signed-evidence agency-documents__history-heading">
                        <div className="agency-documents__history-content">
                          <div className="agency-documents__evidence-label">
                            Signed evidence
                          </div>
                          <div className="agency-documents__history-filename">
                            {v.signedEvidence.fileName}
                          </div>
                          <p className="agency-documents__history-byline">
                            {timestamp(v.signedEvidence.createdAt)}
                          </p>
                        </div>
                        <div className="agency-documents__history-actions">
                          {fileActions(v.signedEvidence)}
                        </div>
                      </div>
                    )}
                  </article>
                ))}
                <DocumentPagination
                  page={versionPage}
                  pages={selected.pagination.versionPages}
                  label="Version history pages"
                  disabled={historyBusy || busy}
                  change={(n) => void select(selected.id, n, eventPage)}
                />
                <h3 className="agency-documents__history-section agency-documents__audit-heading">
                  Audit history
                </h3>
                <ol
                  className="agency-documents__audit-timeline"
                  aria-label="Document audit timeline"
                >
                  {selected.events.map((event) => (
                    <li
                      className="agency-documents__audit-event"
                      key={event.id}
                    >
                      <div className="agency-documents__history-heading">
                        <div className="agency-documents__history-content">
                          <h4 className="agency-documents__event-title">
                            {event.kind.charAt(0).toUpperCase() +
                              event.kind
                                .slice(1)
                                .toLowerCase()
                                .replace(/_/g, " ")}
                          </h4>
                          <p className="agency-documents__history-byline">
                            {event.actorName} · {timestamp(event.createdAt)}
                          </p>
                        </div>
                        {event.fileId && event.fileName && event.mimeType && (
                          <div className="agency-documents__history-actions">
                            {fileActions({
                              fileId: event.fileId,
                              fileName: event.fileName,
                              mimeType: event.mimeType,
                            })}
                          </div>
                        )}
                      </div>
                      <div className="agency-documents__audit-details">
                        <AuditDetails details={event.details} />
                      </div>
                      {event.fileId && event.fileName && event.mimeType && (
                        <div className="agency-documents__audit-attachment agency-documents__history-filename">
                          {event.fileName}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
                <DocumentPagination
                  page={eventPage}
                  pages={selected.pagination.eventPages}
                  label="Audit history pages"
                  disabled={historyBusy || busy}
                  change={(n) => void select(selected.id, versionPage, n)}
                />
              </div>
            </div>
          )}
        </DocumentHistoryDrawer>
      )}
      {settingsDocument && caps.canManageScope && api.settings && (
        <DocumentSettings
          key={settingsDocument.id}
          doc={settingsDocument}
          data={data}
          busy={busy}
          save={(partnership, signature) =>
            mutate(() =>
              api.settings!(settingsDocument.id, {
                partnershipId: partnership,
                requiresSignature: signature,
                operationId: crypto.randomUUID(),
              }),
            ).then(() => setSettingsDocument(null))
          }
          close={() => setSettingsDocument(null)}
        />
      )}
      {form && (
        <DocumentActionModal
          key={`${form.mode}:${form.doc?.id || 0}:${form.version?.id || 0}`}
          form={form}
          defaultPartnershipId={partnershipId}
          data={data}
          busy={busy}
          api={api}
          fileActions={fileActions}
          mutate={mutate}
          error={error}
          cancel={() => {
            setForm(null);
            setError("");
          }}
        />
      )}
      {preview &&
        createPortal(
          <div className="agency-documents agency-documents__preview">
            <div
              ref={dialog}
              className="agency-documents__preview-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="agency-preview-title"
            >
              <div className="d-flex justify-content-between gap-3 mb-3">
                <h2
                  className="agency-documents__text"
                  id="agency-preview-title"
                >
                  {preview.file.fileName}
                </h2>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={closePreview}
                >
                  Close preview
                </button>
              </div>
              {preview.file.mimeType.startsWith("image/") ? (
                <img src={preview.url} alt={preview.file.fileName} />
              ) : preview.file.mimeType === "application/pdf" ? (
                <iframe
                  title={`Preview ${preview.file.fileName}`}
                  src={preview.url}
                />
              ) : (
                <p>
                  This file type cannot be previewed. Use Download to open it
                  with an appropriate application.
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}
function AuditDetails({ details }: { details: unknown }) {
  let value: any = details;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value?.reason ? (
    <p className="mb-0 agency-documents__text">{String(value.reason)}</p>
  ) : value?.userId ? (
    <p className="mb-0">
      Sub-account access changed:{" "}
      {value.canManage ? "Manage" : value.canView ? "View only" : "None"}.
    </p>
  ) : null;
}
function GrantControl({
  grant,
  disabled,
  save,
}: {
  grant: AgencyRegister["grants"][number];
  disabled: boolean;
  save: (access: string, operationId: string) => Promise<void>;
}) {
  const [access, setAccess] = useState(
    grant.canManage ? "manage" : grant.canView ? "view" : "none",
  );
  const operation = useRef("");
  return (
    <form
      className="border-top py-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save(
          access,
          operation.current || (operation.current = crypto.randomUUID()),
        )
          .then(() => {
            operation.current = "";
          })
          .catch(() => undefined);
      }}
    >
      <label
        className="small fw-medium d-block"
        htmlFor={`grant-${grant.userId}`}
      >
        {grant.firstName} {grant.lastName}
        {!grant.isActive ? " (inactive)" : ""}
      </label>
      <div className="d-flex gap-2 mt-2">
        <select
          className="form-select form-select-sm"
          id={`grant-${grant.userId}`}
          value={access}
          disabled={disabled || !grant.isActive}
          onChange={(e) => {
            setAccess(e.target.value);
            operation.current = "";
          }}
        >
          <option value="none">None</option>
          <option value="view">View only</option>
          <option value="manage">Manage</option>
        </select>
        <button
          className="btn btn-sm btn-outline-primary"
          disabled={disabled || !grant.isActive}
        >
          Save
        </button>
      </div>
    </form>
  );
}
function DocumentActionModal(
  props: React.ComponentProps<typeof DocumentForm> & { error: string },
) {
  const label = {
    upload: "Upload document",
    replace:
      props.form.doc && needsSignature(props.form.doc)
        ? "Prepare renewal"
        : "Replace document",
    signed: "Return signed document",
    confirm: "Review signed return",
    archive: "Archive document",
  }[props.form.mode];
  return (
    <DocumentDialog label={label} busy={props.busy} cancel={props.cancel}>
      {props.error && (
        <p role="alert" className="text-danger small px-3 pt-3 mb-0">
          {props.error}
        </p>
      )}
      <DocumentForm {...props} />
    </DocumentDialog>
  );
}
function DocumentDialog({
  label,
  busy,
  cancel,
  children,
}: {
  label: string;
  busy: boolean;
  cancel: () => void;
  children: React.ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const state = useRef({ busy, cancel });
  state.current = { busy, cancel };
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current
      ?.querySelector<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="-1"]',
      )
      ?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const key = (event: KeyboardEvent) => {
      const dialogs = document.querySelectorAll('[aria-modal="true"]');
      if (dialogs[dialogs.length - 1] !== panel.current) return;
      if (event.key === "Escape") {
        event.preventDefault();
        if (!state.current.busy) state.current.cancel();
      }
      if (event.key === "Tab") {
        const nodes = panel.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex="0"]',
        );
        if (!nodes?.length) {
          event.preventDefault();
          return;
        }
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            !Array.from(nodes).includes(document.activeElement as HTMLElement))
        ) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", key);
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return createPortal(
    <div className="agency-documents agency-documents__preview">
      <div
        ref={panel}
        className="agency-documents__archive-panel"
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
function DocumentForm({
  form,
  defaultPartnershipId,
  data,
  busy,
  api,
  mutate,
  cancel,
  fileActions,
}: {
  form: {
    mode: "upload" | "replace" | "signed" | "confirm" | "archive";
    doc?: AgencyDocument;
    version?: AgencyVersion;
  };
  defaultPartnershipId?: number;
  data: AgencyRegister;
  busy: boolean;
  api: AgencyDocumentsApi;
  mutate: (action: () => Promise<unknown>) => Promise<void>;
  cancel: () => void;
  fileActions: (file: AgencyFile) => React.ReactNode;
}) {
  const [title, setTitle] = useState(""),
    [category, setCategory] = useState(form.doc?.category || "REGISTRATION"),
    [signature, setSignature] = useState(
      form.doc ? needsSignature(form.doc) : false,
    ),
    [scope, setScope] = useState(
      String(form.doc?.partnershipId || defaultPartnershipId || ""),
    ),
    [issueDate, setIssueDate] = useState(""),
    [effectiveDate, setEffectiveDate] = useState(""),
    [expiresOn, setExpiresOn] = useState(""),
    [doesNotExpire, setDoesNotExpire] = useState(false),
    [reason, setReason] = useState(""),
    [file, setFile] = useState<File | null>(null),
    [validation, setValidation] = useState("");
  const operation = useRef("");
  const formHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    formHeading.current?.focus();
    formHeading.current?.scrollIntoView?.({ block: "nearest" });
    return () => {
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  const mode = form.mode,
    version = form.version,
    contract = category === "CONTRACT",
    signing = mode === "replace" ? needsSignature(form.doc!) : signature,
    fileNeeded = ["upload", "replace", "signed"].includes(mode),
    metadata = ["upload", "replace"].includes(mode);
  const changed = () => {
    operation.current = "";
    setValidation("");
  };
  const heading =
    mode === "upload"
      ? "Upload document"
      : mode === "replace"
        ? signing
          ? "Prepare renewal"
          : "Replace document"
        : mode === "signed"
          ? "Return signed document"
          : mode === "confirm"
            ? "Confirm signed return"
            : "Archive document?";
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (fileNeeded && (!file || !file.size || file.size > 10 * 1024 * 1024)) {
      setValidation("Choose a non-empty file no larger than 10 MB.");
      return;
    }
    if (
      metadata &&
      ((expiresOn && effectiveDate && expiresOn < effectiveDate) ||
        (expiresOn && issueDate && expiresOn < issueDate) ||
        (effectiveDate && issueDate && effectiveDate < issueDate))
    ) {
      setValidation("Dates must follow issue, effective and expiry order.");
      return;
    }
    const operationId =
      operation.current || (operation.current = crypto.randomUUID());
    const body = new FormData();
    body.append("operationId", operationId);
    if (file) body.append("file", file);
    if (metadata) {
      body.append("title", title.trim());
      body.append("category", category);
      if (data.capabilities.canManageScope && mode === "upload") {
        body.append("requiresSignature", String(signing));
        if (scope) body.append("partnershipId", scope);
      }
      body.append("issueDate", issueDate);
      body.append("effectiveDate", effectiveDate);
      body.append("expiresOn", doesNotExpire ? "" : expiresOn);
      body.append("doesNotExpire", String(doesNotExpire));
      if (mode === "replace") {
        body.append("reason", reason.trim());
        body.append("expectedVersion", String(form.doc!.latestVersion!.number));
      }
    }
    const action =
      mode === "upload" || mode === "replace"
        ? () => api.upload(data.agency.id, body, form.doc?.id)
        : mode === "signed"
          ? () => api.signed(version!.id, body)
          : mode === "confirm"
            ? () =>
                api.confirm(
                  version!.id,
                  operationId,
                  version!.signedEvidence!.id,
                )
            : () => api.archive(form.doc!.id, operationId, reason.trim());
    void mutate(action).catch(() => undefined);
  };
  return (
    <form
      className={`card p-3 mb-4 ${mode === "confirm" ? "agency-documents__confirmation" : ""}`}
      onSubmit={submit}
      onChange={changed}
      aria-label={heading}
    >
      <h2 className="mb-3" ref={formHeading} tabIndex={-1}>
        {heading}
      </h2>
      {form.doc && (
        <p className="small fw-medium agency-documents__text">
          {form.doc.title}
        </p>
      )}
      {mode === "replace" && (
        <p className="small text-muted">
          Replacement retains all previous versions.{" "}
          {contract
            ? "A prepared renewal does not replace the active contract until signed evidence is confirmed and its effective date arrives."
            : "The current version remains unchanged if upload fails."}
        </p>
      )}
      {metadata && (
        <>
          <label className="small d-block mb-3">
            Title *
            <input
              className="form-control form-control-sm mt-1"
              value={form.doc?.title || title}
              readOnly={mode === "replace"}
              required
              maxLength={191}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="small d-block mb-3">
            Category *
            <select
              className="form-select form-select-sm mt-1"
              value={category}
              disabled={busy || mode === "replace"}
              onChange={(e) => {
                setCategory(e.target.value);
                if (e.target.value === "CONTRACT") {
                  setDoesNotExpire(false);
                  setSignature(true);
                }
              }}
            >
              <option value="REGISTRATION">Company registration</option>
              <option value="CERTIFICATE">Certificate</option>
              <option value="IDENTIFICATION">Identification</option>
              <option value="OTHER">Other</option>
              {data.capabilities.canIssueContracts && (
                <>
                  <option value="CONTRACT">Contract</option>
                  <option value="AGREEMENT">Agreement</option>
                </>
              )}
            </select>
          </label>
          {mode === "upload" && data.capabilities.canManageScope && (
            <>
              <label className="small d-block mb-3">
                Document scope
                <select
                  className="form-select form-select-sm mt-1"
                  value={scope}
                  disabled={busy}
                  onChange={(e) => setScope(e.target.value)}
                >
                  <option value="">Agency-wide</option>
                  {data.partnerships?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.institutionName ||
                        p.universities?.name ||
                        `Partnership ${p.id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="small d-flex gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={signature}
                  disabled={busy}
                  onChange={(e) => setSignature(e.target.checked)}
                />
                Requires signature
              </label>
            </>
          )}
          <div className="row g-2">
            <label className="small col-6 mb-3">
              Issue date
              <input
                className="form-control form-control-sm mt-1"
                type="date"
                value={issueDate}
                disabled={busy}
                onChange={(e) => setIssueDate(e.target.value)}
              />
            </label>
            <label className="small col-6 mb-3">
              Effective date{signing || contract ? " *" : ""}
              <input
                className="form-control form-control-sm mt-1"
                type="date"
                required={signing || contract}
                value={effectiveDate}
                disabled={busy}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </label>
          </div>
          <label className="small d-block mb-3">
            Expiry date{!doesNotExpire ? " *" : ""}
            <input
              className="form-control form-control-sm mt-1"
              type="date"
              required={!doesNotExpire}
              disabled={busy || doesNotExpire}
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
            />
          </label>
          {!contract && (
            <label className="small d-flex gap-2 align-items-center mb-3">
              <input
                type="checkbox"
                checked={doesNotExpire}
                disabled={busy}
                onChange={(e) => {
                  setDoesNotExpire(e.target.checked);
                  if (e.target.checked) setExpiresOn("");
                }}
              />
              Does not expire
            </label>
          )}
        </>
      )}
      {version && (
        <div className="small mb-3">
          <DocumentStatus status={version.status} />
          <p className="text-muted mt-2 mb-1">
            v{version.number} · Effective {date(version.effectiveDate)} ·
            Expires {date(version.expiresOn)}
          </p>
          {mode === "signed" && (
            <>
              <div className="mt-2">{fileActions(version)}</div>
              <p className="text-muted mt-2 mb-0">
                Download the unsigned document, sign externally, then upload the
                signed copy. Administrator confirmation is required.
              </p>
            </>
          )}
          {mode === "confirm" && version.signedEvidence && (
            <>
              <p className="mt-2 agency-documents__text">
                Signed evidence: {version.signedEvidence.fileName}
              </p>
              {fileActions(version.signedEvidence)}
              <p className="text-muted mt-2 mb-0">
                Confirmation accepts this signed evidence. Future effective
                dates remain pending until that date.
              </p>
            </>
          )}
        </div>
      )}
      {fileNeeded && (
        <label className="small d-block mb-3">
          {mode === "signed" ? "Signed copy" : "Document"} *
          <input
            className="form-control form-control-sm mt-1"
            type="file"
            required
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            disabled={busy}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <span className="text-muted d-block mt-1">
            PDF, PNG, JPEG or Word · maximum 10 MB.
          </span>
        </label>
      )}
      {["replace", "archive"].includes(mode) && (
        <label className="small d-block mb-3">
          {mode === "replace" ? "Replacement" : "Archive"} reason *
          <textarea
            className="form-control form-control-sm mt-1"
            required
            maxLength={1000}
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      )}
      {mode === "confirm" && api.reject && (
        <label className="small d-block mb-3">
          Reason if rejecting
          <textarea
            className="form-control form-control-sm mt-1"
            maxLength={1000}
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      )}
      {mode === "archive" && (
        <p className="small text-muted">
          Files and version history remain available. This does not permanently
          delete any evidence.
        </p>
      )}
      {validation && (
        <p role="alert" className="small text-danger">
          {validation}
        </p>
      )}
      <div className="d-flex gap-2 flex-wrap">
        <button className="btn btn-sm btn-primary" disabled={busy}>
          {busy
            ? "Saving…"
            : mode === "confirm"
              ? "Confirm signed evidence"
              : mode === "archive"
                ? "Confirm archive"
                : mode === "signed"
                  ? "Return signed copy"
                  : mode === "replace"
                    ? "Save replacement"
                    : "Upload document"}
        </button>
        {mode === "confirm" && api.reject && (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            disabled={busy}
            onClick={() => {
              if (!reason.trim()) {
                setValidation("Provide a rejection reason.");
                return;
              }
              const id =
                operation.current || (operation.current = crypto.randomUUID());
              void mutate(() =>
                api.reject!(
                  version!.id,
                  id,
                  version!.signedEvidence!.id,
                  reason.trim(),
                ),
              ).catch(() => undefined);
            }}
          >
            Reject signed evidence
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={busy}
          onClick={cancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function DocumentHistoryDrawer({
  children,
  footer,
  busy,
  suspended,
  close,
}: {
  children: ReactNode;
  footer?: ReactNode;
  busy: boolean;
  suspended: boolean;
  close: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const state = useRef({ busy, suspended, close });
  state.current = { busy, suspended, close };
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.current?.focus();
    const key = (event: KeyboardEvent) => {
      if (state.current.suspended) return;
      if (event.key === "Escape" && !state.current.busy) {
        event.preventDefault();
        state.current.close();
      }
      if (event.key === "Tab") {
        const nodes = panel.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]',
        );
        if (!nodes?.length) {
          event.preventDefault();
          return;
        }
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === panel.current)
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last ||
            document.activeElement === panel.current)
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return createPortal(
    <div
      className="agency-documents agency-documents__history-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy && !suspended)
          close();
      }}
    >
      <div
        ref={panel}
        className="agency-documents__history-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Document history"
        tabIndex={-1}
      >
        <div className="agency-documents__history-scroll">{children}</div>
        {footer && (
          <footer className="agency-documents__history-footer">{footer}</footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

function DocumentSettings({
  doc,
  data,
  busy,
  save,
  close,
}: {
  doc: AgencyDocument;
  data: AgencyRegister;
  busy: boolean;
  save: (partnership: number | null, signature: boolean) => Promise<void>;
  close: () => void;
}) {
  const [scope, setScope] = useState(String(doc.partnershipId || "")),
    [signature, setSignature] = useState(needsSignature(doc)),
    [error, setError] = useState("");
  return (
    <DocumentDialog label="Scope and signature" busy={busy} cancel={close}>
      <form
        className="card p-3 mb-4"
        aria-label="Document scope and signature"
        onSubmit={(event) => {
          event.preventDefault();
          void save(scope ? Number(scope) : null, signature).catch((e) =>
            setError(documentError(e)),
          );
        }}
      >
        <h2>Scope and signature</h2>
        <p className="small mt-2">
          {doc.title}. Signed or designated agreement evidence cannot be
          reassigned to another partnership.
        </p>
        <label className="small mb-3">
          Document scope
          <select
            className="form-select form-select-sm mt-1"
            value={scope}
            disabled={busy}
            onChange={(e) => setScope(e.target.value)}
          >
            <option value="">Agency-wide</option>
            {data.partnerships?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.institutionName ||
                  p.universities?.name ||
                  `Partnership ${p.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="small d-flex gap-2 mb-3">
          <input
            type="checkbox"
            checked={signature}
            disabled={busy}
            onChange={(e) => setSignature(e.target.checked)}
          />
          Requires signature
        </label>
        {error && (
          <p role="alert" className="small text-danger">
            {error}
          </p>
        )}
        <div className="d-flex gap-2">
          <button disabled={busy} className="btn btn-sm btn-primary">
            Save settings
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={busy}
            onClick={close}
          >
            Cancel
          </button>
        </div>
      </form>
    </DocumentDialog>
  );
}
